/**
 * Tag normalisation, matching what the backend does.
 *
 * The server lowercases, trims and de-duplicates tags before storing them. If
 * the UI does not do the same, a tag typed as "Depot" is shown as "Depot" until
 * the next fetch and then silently becomes "depot" — which reads as the app
 * losing the user's input. Normalising optimistically keeps the two in step, so
 * what you see immediately is what comes back.
 *
 * One place, because two components need the rule and a contract detail should
 * not be restated in each of them.
 */

/** Server rules: lowercase, trimmed, non-empty. */
export function normalizeTag(tag) {
  return String(tag ?? '').trim().toLowerCase();
}

/** A tag list ready to send: normalised, de-duplicated, order preserved. */
export function normalizeTags(tags) {
  if (!Array.isArray(tags)) return [];
  return [...new Set(tags.map(normalizeTag).filter(Boolean))];
}

/** The server's limits, mirrored so the UI can explain a rejection before it
 *  happens rather than surfacing a 400 the user cannot act on. */
export const TAG_MAX_LENGTH = 40;
export const TAGS_MAX_COUNT = 20;

/**
 * Why this tag list would be rejected, or null if it is fine.
 * @param {string[]} tags
 * @returns {string|null}
 */
export function tagsProblem(tags) {
  const normalized = normalizeTags(tags);
  if (normalized.length > TAGS_MAX_COUNT) {
    return `Too many tags — ${normalized.length} of a maximum ${TAGS_MAX_COUNT}.`;
  }
  const tooLong = normalized.find((t) => t.length > TAG_MAX_LENGTH);
  if (tooLong) {
    return `"${tooLong}" is longer than ${TAG_MAX_LENGTH} characters.`;
  }
  return null;
}
